import React from "react";
import AsyncSelect from "react-select/async";
import { buildSelectStyles } from "../utils/selectStyles";

/**
 * DataMappingSingleSelect Component
 *
 * A reusable single-select dropdown component using react-select with single-select
 * functionality for the Data Mapping page.
 *
 * Props:
 * - loadOptions: Function to load options asynchronously
 * - selectedValues: Array of selected values (for consistency with multi-select)
 * - onChange: Function to handle selection changes (values: string[]) => void
 * - placeholder: Optional placeholder text
 * - isDisabled: Optional boolean to disable the dropdown
 * - className: Optional CSS class name
 */
const DataMappingSingleSelect = ({
  loadOptions = [],
  selectedValues = [],
  onChange,
  placeholder = "Select option...",
  isDisabled = false,
  className = "",
}) => {
  const loadingMessage = "Loading Tables...";

  // Ensure selectedValues is always an array and filter out invalid values
  const validSelectedValues = Array.isArray(selectedValues)
    ? selectedValues.filter(
        value => value && typeof value === "string" && value.trim() !== ""
      )
    : [];

  // Convert selectedValues array to react-select format (take first value for single select)
  const selectedOption =
    validSelectedValues.length > 0
      ? { value: validSelectedValues[0], label: validSelectedValues[0] }
      : null;

  // Handle selection changes
  const handleChange = selectedOption => {
    if (!selectedOption) {
      onChange([]);
      return;
    }

    // Extract value from selected option and return as array for consistency
    const values = [selectedOption.value];
    onChange(values);
  };

  // Shared react-select styling — see utils/selectStyles.js.
  const customStyles = buildSelectStyles({ isDisabled });

  return (
    <div className={`data-mapping-singleselect-container ${className}`}>
      <AsyncSelect
        isMulti={false}
        loadOptions={async inputValue => {
          if (typeof loadOptions !== "function") {
            console.error(
              "DataMappingSingleSelect - loadOptions is not a function:",
              loadOptions
            );
            return [];
          }
          try {
            const result = await loadOptions(inputValue);
            return result;
          } catch (error) {
            console.error(
              "DataMappingSingleSelect - loadOptions error:",
              error
            );
            return [];
          }
        }}
        value={selectedOption}
        onChange={handleChange}
        styles={customStyles}
        placeholder={placeholder}
        isClearable={true}
        isSearchable={true}
        isDisabled={isDisabled}
        className={className || "data-mapping-singleselect"}
        classNamePrefix="data-mapping-singleselect"
        noOptionsMessage={() => "No options available"}
        loadingMessage={() => loadingMessage}
        cacheOptions={true}
        defaultOptions={true}
      />
    </div>
  );
};

export default DataMappingSingleSelect;
